import { useEffect, useState } from "react";
import { Loader2, CheckCircle2, CircleAlert } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const StatusPill = ({ configured }) => (
  <span
    data-testid="integration-status-pill"
    className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-outfit font-semibold uppercase tracking-wider ${
      configured ? "bg-sagelight text-sage" : "bg-terra/10 text-terra"
    }`}
  >
    {configured ? <CheckCircle2 className="h-3 w-3" /> : <CircleAlert className="h-3 w-3" />}
    {configured ? "Connected" : "Not configured"}
  </span>
);

export const IntegrationsTab = () => {
  const [pp, setPp] = useState({ configured: false, slug: "", masked_key: "" });
  const [ig, setIg] = useState({ configured: false, ig_user_id: "", masked_token: "" });
  const [ppForm, setPpForm] = useState({ slug: "", api_key: "" });
  const [igForm, setIgForm] = useState({ ig_user_id: "", access_token: "" });
  const [saving, setSaving] = useState("");
  const [ppTest, setPpTest] = useState(null);

  const testPp = async () => {
    setSaving("test");
    setPpTest(null);
    try {
      const r = await api.get("/admin/integrations/propract/test");
      setPpTest(r.data);
    } catch {
      setPpTest({ ok: false, error: "Request failed" });
    } finally {
      setSaving("");
    }
  };

  const load = () => {
    api.get("/admin/integrations/propract/status").then((r) => {
      setPp(r.data);
      setPpForm((f) => ({ ...f, slug: r.data.slug || "" }));
    }).catch(() => {});
    api.get("/admin/integrations/instagram/status").then((r) => {
      setIg(r.data);
      setIgForm((f) => ({ ...f, ig_user_id: r.data.ig_user_id || "" }));
    }).catch(() => {});
  };
  useEffect(load, []);

  const savePp = async () => {
    setSaving("pp");
    try {
      const r = await api.put("/admin/integrations/propract", {
        slug: ppForm.slug,
        api_key: ppForm.api_key || undefined,
      });
      setPp(r.data);
      setPpForm((f) => ({ ...f, api_key: "" }));
      toast.success("ProPract settings saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving("");
    }
  };

  const saveIg = async () => {
    setSaving("ig");
    try {
      const r = await api.put("/admin/integrations/instagram", {
        ig_user_id: igForm.ig_user_id,
        access_token: igForm.access_token || undefined,
      });
      setIg(r.data);
      setIgForm((f) => ({ ...f, access_token: "" }));
      toast.success("Instagram settings saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving("");
    }
  };

  const inputCls = "w-full rounded-xl border border-ink/15 bg-white px-4 py-2.5 text-sm outline-none focus:border-terra";

  return (
    <div data-testid="admin-tab-integrations" className="grid max-w-4xl grid-cols-1 gap-6 lg:grid-cols-2">
      <div className="rounded-2xl border border-ink/10 bg-white p-6">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-display text-xl font-semibold text-ink">ProPract Pro+</h3>
          <StatusPill configured={pp.configured} />
        </div>
        <p className="mb-4 text-xs leading-relaxed text-faint">
          Bookings, courses & events sync. The API key is stored server-side only — never exposed to the website.
        </p>
        <label className="mb-1 block font-outfit text-xs font-semibold uppercase tracking-wider text-faint">Public slug</label>
        <input data-testid="admin-propract-slug" value={ppForm.slug} onChange={(e) => setPpForm({ ...ppForm, slug: e.target.value })} placeholder="e.g. yogyata-wadhwa" className={`${inputCls} mb-3`} />
        <label className="mb-1 block font-outfit text-xs font-semibold uppercase tracking-wider text-faint">
          API key {pp.masked_key && <span className="normal-case text-faint">(current: {pp.masked_key})</span>}
        </label>
        <input data-testid="admin-propract-key" type="password" value={ppForm.api_key} onChange={(e) => setPpForm({ ...ppForm, api_key: e.target.value })} placeholder="Paste Pro+ API key" className={`${inputCls} mb-4`} />
        <div className="flex flex-wrap items-center gap-3">
          <button data-testid="admin-propract-save" onClick={savePp} disabled={saving === "pp"} className="inline-flex items-center gap-2 rounded-full bg-ink px-5 py-2.5 font-outfit text-sm font-semibold text-sand hover:bg-terra disabled:opacity-60">
            {saving === "pp" && <Loader2 className="h-4 w-4 animate-spin" />} Save ProPract
          </button>
          <button data-testid="admin-propract-test" onClick={testPp} disabled={saving === "test"} className="inline-flex items-center gap-2 rounded-full border border-ink/15 px-5 py-2.5 font-outfit text-sm font-semibold text-ink hover:border-terra hover:text-terra disabled:opacity-60">
            {saving === "test" && <Loader2 className="h-4 w-4 animate-spin" />} Test connection
          </button>
        </div>
        {ppTest && (
          <p data-testid="admin-propract-test-result" className={`mt-3 rounded-xl px-4 py-3 text-xs leading-relaxed ${ppTest.ok ? "bg-sagelight text-sage" : "bg-terra/10 text-terra"}`}>
            {ppTest.ok
              ? `Connected — ${ppTest.expert_name || "expert found"} · ${ppTest.services_count} services · ${ppTest.events_count} courses/events live on the site.`
              : `Not connecting: ${ppTest.error}`}
          </p>
        )}
      </div>

      <div className="rounded-2xl border border-ink/10 bg-white p-6">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-display text-xl font-semibold text-ink">Instagram Graph API</h3>
          <StatusPill configured={ig.configured} />
        </div>
        <p className="mb-4 text-xs leading-relaxed text-faint">
          Auto-syncs recent reels from @thebirth.life. Requires an Instagram Business/Creator account connected to a Meta app (long-lived token).
        </p>
        <label className="mb-1 block font-outfit text-xs font-semibold uppercase tracking-wider text-faint">IG user ID</label>
        <input data-testid="admin-instagram-user-id" value={igForm.ig_user_id} onChange={(e) => setIgForm({ ...igForm, ig_user_id: e.target.value })} placeholder="1784..." className={`${inputCls} mb-3`} />
        <label className="mb-1 block font-outfit text-xs font-semibold uppercase tracking-wider text-faint">
          Access token {ig.masked_token && <span className="normal-case text-faint">(current: {ig.masked_token})</span>}
        </label>
        <input data-testid="admin-instagram-token" type="password" value={igForm.access_token} onChange={(e) => setIgForm({ ...igForm, access_token: e.target.value })} placeholder="Long-lived access token" className={`${inputCls} mb-4`} />
        <button data-testid="admin-instagram-save" onClick={saveIg} disabled={saving === "ig"} className="inline-flex items-center gap-2 rounded-full bg-ink px-5 py-2.5 font-outfit text-sm font-semibold text-sand hover:bg-terra disabled:opacity-60">
          {saving === "ig" && <Loader2 className="h-4 w-4 animate-spin" />} Save Instagram
        </button>
      </div>
    </div>
  );
};
